import Utils from './Utils'
import MainConnector from './MainConnector'
import ErrorManager from './ErrorManager'
import DMPCodes from './DMPCodes'

/**
 *
 * TD0.X transactions management
 * @class AccessConnector
 * @extends {MainConnector}
 */
class AccessConnector extends MainConnector {
    /**
     * Transaction TD0.0 getting certified identity of a patient. Mandatory call before other calls to DMP.
     * @param {string} nir = ssn (15 or 16 length)
     * @param {string} birthDate = date of birth for patient with format : YYYYMMDD
     * @returns
     ** { "Ins": { "s_ins": "188102B17295264", "s_insType": "T" }, "s_birthday": "19881012", "s_patientGivenName": "FIGATELLIX", "s_patientName": "CORSE", "s_patientBirthName": "", "s_status": "OK" }
     */
    async getCertifiedIdentity(nir: string, birthDate: string) {
        const requestEndPoint = 'getcertifiedidentity'

        // check nir validity
        if (!Utils.checkNirOrInsIsValid(nir)) {
            ErrorManager.sendError(
                'invalid nir format should be string of 15 or 16 length (source - getCertifiedIdentity)',
            )
        }

        // check birthDate validity
        if (!Utils.checkBirthDate(birthDate)) {
            ErrorManager.sendError(
                'invalid format for birthDate, format should be YYYYMMDD (source - getCertifiedIdentity)',
            )
        }

        const extraEndPointParams = {
            s_nirOD: nir,
            s_birthDate: birthDate,
            i_birthRank: 1, // always one currently will update when begin reading 'carte vitale'
        }
        const result = await this.makeRequest(requestEndPoint, extraEndPointParams)
        return result
    }

    /**
     * Transaction TD0.1 checks if a DMP exists for a patient and if the current EsUser has right on it
     * @param {string} ins = ins with type of patient (info from getCertifiedIdentity)
     * @returns {Promise<Object>}
     ** { "AdminData": { "Ins": { "s_ins": "", "s_insType": "" }, "s_birthday": "", "s_patientBirthName": "", "s_patientGivenName": "", "s_patientName": "" }, "i_dmpStatus": 3, "i_sex": 1, "i_userAuthorization": 1, "i_webAccessIsOpen": 0, "s_closingDate": "", "s_closingMessage": "", "s_status": "OK" } */
    async getDmpStatusInfos(insWithType: string) {
        const requestEndPoint = 'getdmpstatus'

        // check ins
        if (!Utils.checkNirOrInsIsValid(insWithType)) {
            ErrorManager.sendError('invalid ins format should be string of 15 or 16 length (source - getDmpStatus)')
        }

        const extraEndPointParams = {
            s_ins: insWithType,
        }

        const result = await this.makeRequest(requestEndPoint, extraEndPointParams)
        return result
    }

    /**
     * Checks if a DMP exists
     * @param {string} ins with type
     * @returns {boolean}
     */
    async checkIfDmpExists(insWithType: string) {
        // check ins
        if (!Utils.checkNirOrInsIsValid(insWithType))
            ErrorManager.sendError('invalid ins format should be string of 15 or 16 length (source - checkIfDmpExists)')

        const dmpCheckAnswer = await this.getDmpStatusInfos(insWithType)

        if (dmpCheckAnswer.i_dmpStatus === DMPCodes.DMPStatus.DMPExist) return true
        return false
    }

    /**
     * Checks if a DMP is closed or not existing
     * @param {string} ins with type
     * @returns {number} : 1 = exist, 2 = closed, 3 = not existing, 4 = error
     */
    async getDmpStatusCode(insWithType: string) {
        // check ins = nir validity
        if (!Utils.checkNirOrInsIsValid(insWithType)) {
            ErrorManager.sendError('invalid ins format should be string of 15 or 16 length (source - getDmpStatusCode)')
        }

        const dmpCheckAnswer = await this.getDmpStatusInfos(insWithType)

        return dmpCheckAnswer.i_dmpStatus
    }

    /**
     * Adds an authorisation for DMP access
     * @param {string} ins with type
     * @returns {Promise<Object>} : the result of the request
     */
    async addAuthorisationForDmp(insWithType: string) {
        const requestEndPoint = 'updatedmpaccessauthorization'

        // check ins
        if (!Utils.checkNirOrInsIsValid(insWithType)) {
            ErrorManager.sendError(
                'invalid ins format should be string of 15 or 16 length (source - addAuthorisationForDmp)',
            )
        }

        const extraEndPointParams = {
            s_ins: insWithType,
            i_action: DMPCodes.UserAuthorizationAction.AddAuthorization,
        }

        return this.makeRequest(requestEndPoint, extraEndPointParams)
    }

    /**
     * Transaction TD0.9 : Get WebPS URL list
     * @param {string} insWithType patient ins num with type
     * @returns {Promise<Object>} : the result of the request
     */
    async getWebPsDmpUrls(insWithType: string) {
        const requestEndPoint = 'getwebpsdmpurls'

        // check ins
        if (!Utils.checkNirOrInsIsValid(insWithType)) {
            ErrorManager.sendError(
                'invalid ins format should be string of 15 or 16 length (source - getWebPsDmpUrls)',
            )
        }

        const extraEndPointParams = {
            s_ins: insWithType,
        }

        return this.makeRequest(requestEndPoint, extraEndPointParams)
    }
}

export default AccessConnector
